import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useFacilities } from "@/context/FacilityContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash, Plus, CheckCircle, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Targets() {
  const { t } = useLang();
  const { facilities } = useFacilities();
  const [targets, setTargets] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    name: "",
    baseline_year: new Date().getFullYear() - 2,
    target_year: new Date().getFullYear() + 5,
    reduction_pct: 42,
    scopes: [1, 2],
    target_type: "absolute",
    status: "near-term",
    residual_pct: 10,
    facility_id: "",
  });

  const load = async () => {
    const tg = await api.get("/targets");
    setTargets(tg.data);
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/targets", {
        ...form,
        reduction_pct: parseFloat(form.reduction_pct),
        baseline_year: parseInt(form.baseline_year),
        target_year: parseInt(form.target_year),
        residual_pct: parseFloat(form.residual_pct || 0),
        status: form.target_type === "net_zero" ? "net-zero" : form.status,
        facility_id: form.facility_id || null,
      });
      toast.success(t("targetCreated"));
      setShowForm(false);
      setForm({ ...form, name: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const remove = async (id) => {
    await api.delete(`/targets/${id}`);
    toast.success(t("targetDeleted"));
    load();
  };

  const toggleScope = (s) => {
    setForm((f) => ({ ...f, scopes: f.scopes.includes(s) ? f.scopes.filter((x) => x !== s) : [...f.scopes, s].sort() }));
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-end flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data">{t("sbtiAligned")}</div>
          <h1 className="font-display text-3xl font-semibold mt-1">{t("targets")}</h1>
        </div>
        <Button onClick={() => setShowForm((s) => !s)} data-testid="target-toggle">
          <Plus size={16} className="mr-1" /> {showForm ? t("cancel") : t("newTarget")}
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardContent className="pt-6">
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="target-form">
              <div className="md:col-span-2 space-y-2">
                <Label>{t("targetName")}</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Near-term 1.5°C 2030" data-testid="target-name" />
              </div>
              <div className="space-y-2">
                <Label>{t("baselineYear")}</Label>
                <Input type="number" required value={form.baseline_year} onChange={(e) => setForm({ ...form, baseline_year: e.target.value })} data-testid="target-baseline" />
              </div>
              <div className="space-y-2">
                <Label>{t("targetYear")}</Label>
                <Input type="number" required value={form.target_year} onChange={(e) => setForm({ ...form, target_year: e.target.value })} data-testid="target-year" />
              </div>
              <div className="space-y-2">
                <Label>{t("reductionPct")}</Label>
                <Input type="number" step="0.1" required value={form.reduction_pct} onChange={(e) => setForm({ ...form, reduction_pct: e.target.value })} data-testid="target-reduction" />
                <div className="text-[11px] text-muted-foreground">SBTi 1.5°C near-term: 42% by 2030 (4.2%/yr)</div>
              </div>
              <div className="space-y-2">
                <Label>{t("targetType")}</Label>
                <Select value={form.target_type} onValueChange={(v) => setForm({ ...form, target_type: v, reduction_pct: v === "net_zero" ? 100 : form.reduction_pct })}>
                  <SelectTrigger data-testid="target-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="absolute">{t("absoluteTarget")}</SelectItem>
                    <SelectItem value="intensity_revenue">{t("intensityRevenue")}</SelectItem>
                    <SelectItem value="intensity_fte">{t("intensityFte")}</SelectItem>
                    <SelectItem value="intensity_area">{t("intensityArea")}</SelectItem>
                    <SelectItem value="net_zero">{t("netZero")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.target_type === "net_zero" && (
                <div className="space-y-2">
                  <Label>{t("residual")}</Label>
                  <Input type="number" step="0.1" value={form.residual_pct} onChange={(e) => setForm({ ...form, residual_pct: e.target.value })} data-testid="target-residual" />
                  <div className="text-[11px] text-muted-foreground">{t("residualHint")}</div>
                </div>
              )}
              <div className="space-y-2 md:col-span-2">
                <Label>{t("targetScope")}</Label>
                <Select value={form.facility_id || "all"} onValueChange={(v) => setForm({ ...form, facility_id: v === "all" ? "" : v })}>
                  <SelectTrigger data-testid="target-facility"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("orgWide")}</SelectItem>
                    {facilities.map((f) => (
                      <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2 space-y-2">
                <Label>{t("scopesCovered")}</Label>
                <div className="flex gap-2">
                  {[1, 2, 3].map((s) => (
                    <button
                      type="button"
                      key={s}
                      onClick={() => toggleScope(s)}
                      className={`px-4 py-2 border rounded-md font-mono-data text-sm transition-colors ${form.scopes.includes(s) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}
                      data-testid={`target-scope-${s}`}
                    >
                      Scope {s}
                    </button>
                  ))}
                </div>
              </div>
              <div className="md:col-span-2">
                <Button type="submit" data-testid="target-submit">{t("createTarget")}</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {targets.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center text-muted-foreground text-sm">{t("noTargets")}</CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {targets.map((tg) => {
            const progress = Math.max(0, Math.min(100, tg.progress_pct));
            const isIntensity = tg.target_type && tg.target_type.startsWith("intensity_");
            const unit = tg.unit || (tg.is_net_zero ? t("tCO2e") : t("tCO2e"));
            const baseline = isIntensity ? tg.baseline_value : tg.baseline_emissions_t_co2e;
            const current = isIntensity ? tg.current_value : tg.current_emissions_t_co2e;
            const target = isIntensity ? tg.target_value : tg.target_emissions_t_co2e;

            if (tg.computable === false) {
              return (
                <Card key={tg.id} data-testid={`target-card-${tg.id}`} className="border-accent/30">
                  <CardHeader>
                    <CardTitle className="font-display text-lg">{tg.name}</CardTitle>
                    <div className="text-xs text-muted-foreground font-mono-data">
                      {tg.baseline_year} → {tg.target_year} · {t(targetTypeLabel(tg.target_type))}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-sm text-accent flex items-start gap-2">
                      <Warning size={16} weight="fill" className="mt-0.5 shrink-0" />
                      <span>{t("notComputable")}. {tg.message}</span>
                    </div>
                    <button onClick={() => remove(tg.id)} className="mt-3 text-xs text-muted-foreground hover:text-destructive" data-testid={`target-delete-${tg.id}`}>
                      <Trash size={12} className="inline mr-1" /> {t("delete")}
                    </button>
                  </CardContent>
                </Card>
              );
            }

            return (
              <Card key={tg.id} data-testid={`target-card-${tg.id}`}>
                <CardHeader className="flex flex-row items-start justify-between space-y-0">
                  <div>
                    <CardTitle className="font-display text-lg">{tg.name}</CardTitle>
                    <div className="text-xs text-muted-foreground mt-1 font-mono-data">
                      {tg.baseline_year} → {tg.target_year} ·{" "}
                      {tg.is_net_zero ? `${t("netZero")} · residual ${tg.residual_pct}%` : `−${tg.reduction_pct}%`} ·{" "}
                      {tg.scopes.map((s) => `S${s}`).join(" + ")} · {t(targetTypeLabel(tg.target_type))}
                      {tg.facility_id && facilities.find((f) => f.id === tg.facility_id) && (
                        <span className="ml-1 text-primary">· {facilities.find((f) => f.id === tg.facility_id).name}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {tg.on_track ? (
                      <span className="inline-flex items-center gap-1 text-xs font-mono-data uppercase text-primary bg-primary/10 px-2 py-1 rounded-sm">
                        <CheckCircle size={14} weight="fill" /> {t("onTrack")}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-mono-data uppercase text-destructive bg-destructive/10 px-2 py-1 rounded-sm">
                        <Warning size={14} weight="fill" /> {t("offTrack")}
                      </span>
                    )}
                    <button onClick={() => remove(tg.id)} className="text-muted-foreground hover:text-destructive" data-testid={`target-delete-${tg.id}`}>
                      <Trash size={16} />
                    </button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <Metric label={t("baseline")} value={baseline} suffix={unit} />
                    <Metric label={t("current")} value={current} suffix={unit} />
                    <Metric label={t("target")} value={target} suffix={unit} accent />
                  </div>
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-muted-foreground uppercase tracking-widest font-mono-data">{t("progress")}</span>
                      <span className="font-mono-data font-medium">{tg.progress_pct.toFixed(1)}%</span>
                    </div>
                    <div className="h-3 bg-secondary rounded-sm overflow-hidden">
                      <div className={`h-full ${tg.on_track ? "bg-primary" : "bg-accent"}`} style={{ width: `${progress}%` }} />
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-2 font-mono-data">
                      {t("annualRequired")}: {tg.sbti_annual_required_pct}%/yr
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

const Metric = ({ label, value, suffix, accent }) => (
  <div className={`p-3 rounded-md border ${accent ? "border-primary/40 bg-primary/5" : "border-border"}`}>
    <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono-data">{label}</div>
    <div className="font-display text-xl font-semibold mt-1 font-mono-data">{value?.toFixed?.(2) ?? value ?? "—"}</div>
    <div className="text-[10px] text-muted-foreground font-mono-data">{suffix}</div>
  </div>
);

function targetTypeLabel(tt) {
  return {
    absolute: "absoluteTarget",
    intensity_revenue: "intensityRevenue",
    intensity_fte: "intensityFte",
    intensity_area: "intensityArea",
    net_zero: "netZero",
  }[tt] || "absoluteTarget";
}
